



























import { Component, Prop, Watch, Ref, Mixins } from 'vue-property-decorator';
import SInputBase from '@/components/UI/Form/SInputBase.vue';
import SFormElement from './SFormElement.vue';
import Decimal, { isDecimal } from '@/utils/decimal';
import { WatchObjectAsJson } from '@/decorators/watch-object-as-json';

// if an SAmountInput contains any of these strings. they will not be
// formatted until the user blurs the input
const IGNORELIST = [ '.', '-.', ',', '-,' ];

@Component({
  components: {
    SInputBase,
  },
})
export default class SAmountInput extends Mixins(SFormElement) {
  @Prop([ Object ]) public value!: Decimal | null;
  @Prop(Boolean) private allowBlank!: boolean;
  @Prop(Boolean) private onlyPositive!: boolean;
  @Ref() public readonly inputRef!: SInputBase;

  private internalValue = this.toFormatted(this.value);

  get listeners() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { input, ...listeners } = this.$listeners;
    return listeners;
  }

  public focus() {
    this.inputRef.focus();
  }

  public select() {
    this.inputRef.select();
  }

  public blur() {
    this.inputRef.blur();
  }

  public selectAllButFirst() {
    this.inputRef.selectAllButFirst();
  }

  @Watch('$i18n.locale')
  public formatValue() {
    this.internalValue = this.toFormatted(this.value, true);
  }

  @WatchObjectAsJson('value')
  private updateInternalValue() {
    const decimalValue = this.toDecimal(this.internalValue);

    // if both new value and current internal value are set, are decimals and are equal,
    // do not trigger the change.
    if (
      this.value &&
      decimalValue && typeof decimalValue !== 'string' &&
      this.value.equals(decimalValue)
    ) return;

    this.internalValue = this.toFormatted(this.value);
  }

  @Watch('internalValue')
  private updateValue(): void {
    const decimalValue = this.toDecimal(this.internalValue);

    if (typeof decimalValue === 'string') return;
    if (this.value && decimalValue && decimalValue.equals(this.value)) return;

    this.$emit('input', decimalValue);
  }

  private handleNumpadDecimal(event: KeyboardEvent) {
    const delimiter = this.$i18n.t('delimiter') as string;

    event.preventDefault();

    const input = event.target as HTMLInputElement;
    const caretBegin = input.selectionStart || 0;
    const caretEnd = input.selectionEnd || 0;

    const before = input.value.slice(0, caretBegin);
    const after = input.value.slice(caretEnd);
    const newValue = before + delimiter + after;
    this.internalValue = newValue;

    this.$nextTick(() => input.setSelectionRange(caretBegin + 1, caretBegin + 1));
  }

  private toDecimal(value: Decimal | string, force = false): Decimal | string | null {
    if (!value) return this.allowBlank ? null : new Decimal(0);
    if (isDecimal(value)) return value;

    value = value as string;

    // This is a minus, not a hyphen-minus, and cannot be parsed correctly
    // For more info, check commit message on commit 6030779d
    if (value[0] === '−') value = '-' + value.substring(1);
    if (IGNORELIST.includes(value)) return force ? new Decimal(0) : value;
    const delimiter = (this.$t('delimiter') as string);
    let fragments = '0';

    const values: string[] = value.split(delimiter);

    if (values.length > 1) fragments = (values.pop() as string);

    const newValue = [ values.join(''), fragments ].map(str => str.replace(/[^\d-]/g, '')).join('.');
    const multiplier = this.onlyPositive ? 1 : (newValue[0] === '-' ? -1 : 1);
    return new Decimal(newValue.replace('-', '')).times(multiplier);
  }

  private toFormatted(value: Decimal | string | null, force = false): string {
    if (!value) return this.allowBlank ? '' : this.$n(0, 'currency');

    const numVal = this.toDecimal(value, force)!;
    if (typeof numVal === 'string') return numVal;
    return this.$n(numVal.toNumber(), 'currency') || `${value}`;
  }
}
